import {
  registerDecorator,
  validateSync,
  type ValidationOptions,
  type ValidationArguments,
  type ValidationError,
} from 'class-validator';
import { formatValidationErrors } from '@builder4/shared/utils/validators';

// Store validation errors keyed by the parent object for error message formatting
const validationErrorsMap = new WeakMap<object, ValidationError[]>();

/**
 * Validates all values in a Record<string, T> object by running class-validator
 * validation on each value. This is needed because @ValidateNested({ each: true })
 * only works with arrays, not objects/records.
 */
export function ValidateNestedRecord(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'ValidateNestedRecord',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate(value: any, args: ValidationArguments) {
          if (typeof value !== 'object' || value === null) {
            return false;
          }

          const allErrors: ValidationError[] = [];

          for (const [key, item] of Object.entries(value)) {
            if (typeof item !== 'object' || item === null) {
              return false;
            }
            const errors = validateSync(item);
            if (errors.length > 0) {
              // Prefix each error with the key for context
              errors.forEach((error) => {
                error.property = `${key}.${error.property}`;
              });
              allErrors.push(...errors);
            }
          }

          if (allErrors.length > 0) {
            validationErrorsMap.set(args.object, allErrors);
            return false;
          }

          return true;
        },
        defaultMessage(args: ValidationArguments) {
          const stored = validationErrorsMap.get(args.object);

          if (stored && stored.length > 0) {
            const errorMessages = formatValidationErrors(stored);
            validationErrorsMap.delete(args.object);
            return `Validation failed for ${args.property}:\n${errorMessages.join('\n')}`;
          }

          return `One or more values in ${args.property} failed validation`;
        },
      },
    });
  };
}
